/**
 * The filter bar above the tournament grid. Search stays visible at every
 * width; on phones the dropdowns fold behind a Filters toggle so the list
 * itself is what fills the screen.
 */
import { useState } from "react";
import {
  isFiltered,
  type FacetOption,
  type SortOrder,
  type TournamentFilters,
} from "../lib/tournamentSearch";
import { Button, Input, SearchIcon, Select, XIcon, cx } from "./ui";

interface Props {
  filters: TournamentFilters;
  sort: SortOrder;
  states: FacetOption[];
  divisions: FacetOption[];
  onFilters: (next: TournamentFilters) => void;
  onSort: (next: SortOrder) => void;
  onClear: () => void;
}

const selectCls = "w-full sm:w-auto";

export function TournamentFilterBar({
  filters,
  sort,
  states,
  divisions,
  onFilters,
  onSort,
  onClear,
}: Props) {
  const [open, setOpen] = useState(false);
  const set = <K extends keyof TournamentFilters>(key: K, value: TournamentFilters[K]) =>
    onFilters({ ...filters, [key]: value });

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="relative min-w-0 flex-1">
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            type="search"
            value={filters.query}
            onChange={(e) => set("query", e.target.value)}
            aria-label="Search tournaments"
            placeholder="Search by name, division, city, or state"
            className="pl-9 pr-9 [&::-webkit-search-cancel-button]:hidden"
          />
          {filters.query ? (
            <button
              type="button"
              onClick={() => set("query", "")}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-600"
            >
              <XIcon className="h-3.5 w-3.5" />
            </button>
          ) : null}
        </div>
        <Button
          type="button"
          variant="secondary"
          className="shrink-0 sm:hidden"
          aria-expanded={open}
          aria-controls="tournament-filters"
          onClick={() => setOpen((v) => !v)}
        >
          Filters
        </Button>
      </div>

      <div
        id="tournament-filters"
        className={cx(
          "gap-2 sm:flex sm:flex-wrap sm:items-center",
          open ? "grid grid-cols-2" : "hidden",
        )}
      >
        <Select
          className={selectCls}
          aria-label="Filter by state"
          value={filters.state}
          onChange={(e) => set("state", e.target.value)}
        >
          <option value="all">All states</option>
          {states.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label} ({s.count})
            </option>
          ))}
        </Select>

        <Select
          className={selectCls}
          aria-label="Filter by division"
          value={filters.division}
          onChange={(e) => set("division", e.target.value)}
        >
          <option value="all">All divisions</option>
          {divisions.map((d) => (
            <option key={d.value} value={d.value}>
              {d.label} ({d.count})
            </option>
          ))}
        </Select>

        <Select
          className={selectCls}
          aria-label="Filter by registration status"
          value={filters.registration}
          onChange={(e) => set("registration", e.target.value === "open" ? "open" : "all")}
        >
          <option value="all">Any registration</option>
          <option value="open">Registration open</option>
        </Select>

        <Select
          className={selectCls}
          aria-label="Filter by timeframe"
          value={filters.timeframe}
          onChange={(e) => {
            const v = e.target.value;
            set("timeframe", v === "past" || v === "all" ? v : "upcoming");
          }}
        >
          <option value="upcoming">Upcoming</option>
          <option value="past">Past</option>
          <option value="all">All dates</option>
        </Select>

        <Select
          className={cx(selectCls, "col-span-2 sm:col-span-1")}
          aria-label="Sort tournaments"
          value={sort}
          onChange={(e) => onSort(e.target.value === "name" ? "name" : "date")}
        >
          <option value="date">Soonest first</option>
          <option value="name">Name (A–Z)</option>
        </Select>

        {isFiltered(filters) || sort !== "date" ? (
          <Button type="button" variant="ghost" size="sm" onClick={onClear} className="col-span-2">
            Clear filters
          </Button>
        ) : null}
      </div>
    </div>
  );
}
